import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Link,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ContractTransaction } from 'ethers';
import { orderBy } from 'lodash';
import { NextSeo } from 'next-seo';
import NextLink from 'next/link';
import { useState } from 'react';
import useSWR from 'swr';
import Footer from '../components/Footer';
import TransactionWaitModal from '../components/modals/TransactionWaitModal';
import { IOpenStore__factory, SongADay__factory } from '../types';
import {
  BURN_COLLECTION,
  OPENSTORE_CONTRACT_ADDRESS,
  SONGADAY_CONTRACT_ADDRESS,
} from '../utils/constants';
import { validateBeforeWriting } from '../utils/helpers';
import { parseErrorMessage } from '../web3/helpers';
import { useContract, useReadContract, useWriteContract } from '../web3/hooks';
import { useWallet } from '../web3/WalletContext';

export default function Claim() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [txHash, setTxHash] = useState<string | undefined>();
  const approval = true;

  const { contract: openStoreContract } = useContract(
    OPENSTORE_CONTRACT_ADDRESS,
    IOpenStore__factory,
  );
  const { contract: songADayContract } = useContract(SONGADAY_CONTRACT_ADDRESS, SongADay__factory);

  const handleConfirmation = async () => {
    await refreshOldSongs();
    toast({
      status: 'success',
      title: 'Claimed',
      description: 'Your song(s) will be available on the my songs page in a few minutes.',
    });
    onClose();
  };

  const handleApproveConfirmation = async () => {
    await refreshIsApprovedForAllStatus();
    toast({
      status: 'success',
      title: 'Approved',
      description: 'Your song(s) are now approved and can be claimed!',
    });
    onClose();
  };

  const toast = useToast();

  const handleMint = async (response: ContractTransaction) => {
    setTxHash(response.hash);
    console.log('Waiting for transaction to finish');
  };

  const handleError = (error: any) => {
    toast({
      status: 'error',
      description: parseErrorMessage(error),
    });
    onClose();
    console.log(error);
  };

  const [setApprovalForAll] = useWriteContract(openStoreContract, 'setApprovalForAll', {
    onConfirmation: handleApproveConfirmation,
    onError: handleError,
    onResponse: handleMint,
  });

  const { address, chainId, connectWallet } = useWallet();
  const { response: isApprovedForAll, mutate: refreshIsApprovedForAllStatus } = useReadContract(
    openStoreContract,
    'isApprovedForAll',
    undefined,
    address ?? '',
    SONGADAY_CONTRACT_ADDRESS,
  );

  const [publicClaim] = useWriteContract(songADayContract, 'publicClaim', {
    onConfirmation: handleConfirmation,
    onError: handleError,
    onResponse: handleMint,
  });

  const { data: results, mutate: refreshOldSongs } = useSWR(
    `/api/openseaAPIProxy?${new URLSearchParams({
      owner: address,
      collection: BURN_COLLECTION,
      limit: '50',
    })}`,
  );

  const oldSongs = orderBy(results?.assets ?? [], ['token_id'], ['asc']);

  const getPublicClaimParams = async (
    openStoreIds: string[],
  ): Promise<{ tokenIds: number[]; proofs: Array<Array<string>>; openStoreIds: string[] }> => {
    const response = await fetch(`/api/public-claim-params`, {
      method: 'POST',
      body: JSON.stringify({ ids: openStoreIds }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const results = await response.json();
    return results;
  };

  const openStoreIds = oldSongs.map((song) => song.token_id);
  console.log({ openStoreIds });

  return (
    <>
      <NextSeo
        title="Upgrade Songs | Song a Day World"
        description="Check out your collecton of JMann's songs"
        twitter={{
          cardType: 'summary_large_image',
          handle: '@songadaymann',
          site: 'Song a Day World',
        }}
        openGraph={{
          images: [{ url: 'https://songaday.world/assets/music-rainbow.png' }],
        }}
      />
      <Container maxW="container.md" my={16}>
        <Heading
          as="h1"
          fontSize={{ base: '3xl', md: '5xl' }}
          fontWeight="regular"
          letterSpacing="tight"
        >
          Upgrade Songs 🔼
        </Heading>
      </Container>
      <Container maxW="container.lg" my={16}>
        {address && oldSongs?.length > 0 && (
          <>
            <Text
              size="sm"
              letterSpacing="wide"
              casing="uppercase"
              fontWeight="semibold"
              color="gray.500"
            >
              Upgradable songs
            </Text>
            {oldSongs?.map((song) => {
              return (
                <Flex
                  justify="space-between"
                  p="4"
                  rounded="md"
                  key={song.token_id}
                  maxWidth="lg"
                  my={2}
                  bgColor="brand.lightTeal"
                >
                  <Flex align="start" direction="column">
                    <Text>#{song?.external_link.split('/')[4]}</Text>
                    <Text fontWeight="semibold" fontSize="xl">
                      {song?.name.split('|')[0].trim()}
                    </Text>
                    <NextLink href={song?.permalink} passHref>
                      <Link
                        isExternal
                        color="teal.500"
                        textDecoration="underline"
                        aria-label="See bid on etherscan"
                        textAlign="center"
                        mt={-1}
                      >
                        View on Opensea
                      </Link>
                    </NextLink>
                  </Flex>
                  <Flex align="center" justify="center">
                    <Text pr="3"></Text>
                  </Flex>
                </Flex>
              );
            })}

            {isApprovedForAll ? (
              <Button
                size="lg"
                style={{ color: 'white' }}
                onClick={async () => {
                  try {
                    setTxHash(undefined);
                    await validateBeforeWriting(address, chainId, toast);
                    onOpen();
                    const {
                      tokenIds,
                      proofs,
                      openStoreIds: orderedIds,
                    } = await getPublicClaimParams(openStoreIds);
                    const estimatedGas = await songADayContract.estimateGas.publicClaim(
                      orderedIds,
                      tokenIds,
                      proofs,
                    );

                    await publicClaim(orderedIds, tokenIds, proofs, {
                      gasLimit: parseInt((1.2 * estimatedGas.toNumber()).toString()),
                    });
                  } catch (e) {
                    console.log(e);
                  } finally {
                    onClose();
                  }
                }}
              >
                Upgrade {oldSongs.length === 1 ? 'song' : 'songs'}
              </Button>
            ) : (
              <>
                <Stack spacing={1}>
                  <Button
                    size="lg"
                    style={{ color: 'white' }}
                    alignSelf="start"
                    onClick={async () => {
                      try {
                        setTxHash(undefined);
                        await validateBeforeWriting(address, chainId, toast);
                        onOpen();
                        await setApprovalForAll(SONGADAY_CONTRACT_ADDRESS, approval);
                      } catch (e) {
                        console.log(e);
                      } finally {
                        onClose();
                      }
                    }}
                  >
                    Approve {oldSongs.length === 1 ? 'song' : 'songs'}
                  </Button>
                  <Text fontSize="sm" display="flex" alignItems="center" gridGap={2}>
                    <InfoIcon /> Your {oldSongs.length === 1 ? 'song' : 'songs'} will be approved
                    before {oldSongs.length === 1 ? 'it' : 'they'} can be upgraded. It's super
                    quick!
                  </Text>
                </Stack>
              </>
            )}
          </>
        )}

        {address && oldSongs?.length < 1 && (
          <Stack spacing={3}>
            <Text fontSize="2xl" fontWeight="semibold">
              Doesn't look like you have any songs you can upgrade...
            </Text>
            <Text>
              If you just claimed songs you might want to check if they show up in your song
              collection in a bit
            </Text>
            <Link
              href={`/a/${address}`}
              _hover={{
                textDecoration: 'none',
              }}
            >
              <Button size="lg" justifySelf="start" alignSelf="start">
                See my songs
              </Button>
            </Link>
          </Stack>
        )}
        {!address && (
          <Stack spacing={3}>
            <Text>
              Hey there! Connect your wallet so we can check if you have any upgradeable songs!
            </Text>
            <Text>Not sure what this is about? Read on below.</Text>
            <Button size="lg" onClick={connectWallet} justifySelf="start" alignSelf="start">
              Connect
            </Button>
          </Stack>
        )}
      </Container>

      <Box w="full" bgColor="brand.lightTeal" p={16}>
        <Container maxW="container.md" centerContent>
          <Stack spacing={4}>
            <Heading as="h2" fontSize="3xl">
              Joining SongADAO with a song from Year 1 or 2?
            </Heading>
            <Text>
              So, you have an S.A.D NFT from Year 1 or 2. That 's amazing! Thanks to you, the rest
              of this project, from this website to the DAO, has been able to be built.
            </Text>
            <Text>
              Now it 's time to “upgrade” those old NFTs, which were minted on OpenSea 's shared
              storefront contract. It was a necessary step but less than ideal. When you hit
              upgrade, you 'll be burning those old tokens to get a new one that 's on S.A.D 's
              brand new, custom contract! Woot!
            </Text>
            <Text>
              Eventually, the custom token will be the only one that works in the DAO. There will be
              a grace period, but no time like the present, right? Let 's get those tokens upgraded!
            </Text>
            If you have Year 1 or 2 songs but don 't see them here, they 've already been
            upgraded--you 're good to go!
          </Stack>
        </Container>
      </Box>
      <Footer />
      <TransactionWaitModal isOpen={isOpen} onClose={onClose} txHash={txHash} />
    </>
  );
}
